<script lang="ts">
  /**
   * SyncIndicator.svelte — Git sync status indicator with text labels
   *
   * States:
   * - Synced:    ● Synced          (green)
   * - Syncing:   ● Syncing...      (blue, pulsing)
   * - Pending:   ● 3 pending       (orange)
   * - Offline:   ● Offline         (gray, + pending count)
   * - Error:     ● Error           (red, clickable for details)
   *
   * Hidden when sync is disabled.
   */

  import {
    syncStatus,
    pendingChangesCount,
    lastSyncedAt,
    lastSyncError,
    isOnline,
    syncNow,
  } from '../../services/gitSyncService';
  import { i18n } from '../../stores/i18nStore';

  /** Error popover open state */
  let showErrorDetails = false;

  /** Whether a retry is in progress */
  let retrying = false;

  function toggleErrorDetails() {
    showErrorDetails = !showErrorDetails;
  }

  function closeErrorDetails() {
    showErrorDetails = false;
  }

  async function retry() {
    retrying = true;
    showErrorDetails = false;
    try {
      await syncNow();
    } finally {
      retrying = false;
    }
  }

  /** Determine the display state (single source of truth for rendering) */
  type DisplayState = 'syncing' | 'synced' | 'pending' | 'offline' | 'error';

  $: displayState = ((): DisplayState => {
    if ($syncStatus === 'syncing' || retrying) return 'syncing';
    if ($syncStatus === 'error') return 'error';
    if ($syncStatus === 'offline' || !$isOnline) return 'offline';
    if ($pendingChangesCount > 0) return 'pending';
    return 'synced';
  })();

  $: label = ((): string => {
    switch (displayState) {
      case 'syncing':
        return $i18n('sync.syncing');
      case 'error':
        return $i18n('sync.error');
      case 'offline':
        return $pendingChangesCount > 0
          ? $i18n('sync.offlinePending').replace('{count}', String($pendingChangesCount))
          : $i18n('sync.offline');
      case 'pending':
        return $i18n('sync.pending').replace('{count}', String($pendingChangesCount));
      case 'synced':
        return $lastSyncedAt ? $i18n('sync.lastSynced') : $i18n('sync.idle');
    }
  })();
</script>

<svelte:window on:click={() => showErrorDetails && closeErrorDetails()} />

{#if $syncStatus !== 'disabled'}
  <div class="sync-wrapper" class:has-popover={showErrorDetails}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <span
      class="sync-indicator"
      class:syncing={displayState === 'syncing'}
      class:synced={displayState === 'synced'}
      class:pending={displayState === 'pending'}
      class:offline={displayState === 'offline'}
      class:error={displayState === 'error'}
      class:clickable={displayState === 'error'}
      role={displayState === 'error' ? 'button' : undefined}
      tabindex={displayState === 'error' ? 0 : undefined}
      on:click|stopPropagation={displayState === 'error' ? toggleErrorDetails : undefined}
      on:keydown={displayState === 'error' ? (e) => e.key === 'Enter' && toggleErrorDetails() : undefined}
    >
      <span class="dot" aria-hidden="true"></span>
      <span class="label">{label}</span>
    </span>

    <!-- Error details popover -->
    {#if showErrorDetails && displayState === 'error'}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div class="error-popover" role="alert" on:click|stopPropagation>
        <div class="error-popover-header">
          <span class="error-popover-title">{$i18n('sync.error')}</span>
          <button class="error-close" on:click={closeErrorDetails} aria-label="Close">&times;</button>
        </div>
        {#if $lastSyncError}
          <p class="error-message">{$lastSyncError}</p>
        {/if}
        <button class="error-retry" on:click={retry} disabled={retrying}>
          {$i18n('sync.retry')}
        </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .sync-wrapper {
    position: relative;
  }

  .sync-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    transition: background 0.2s, color 0.2s;
    user-select: none;
  }

  .clickable {
    cursor: pointer;
  }

  .clickable:hover {
    filter: brightness(0.9);
  }

  /* Dot */
  .dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }

  .label {
    letter-spacing: 0.01em;
  }

  /* --- State colors --- */

  .synced {
    color: var(--success-color, #16a34a);
    background: color-mix(in srgb, var(--success-color, #16a34a) 10%, transparent);
  }

  .syncing {
    color: var(--accent-color, #3b82f6);
    background: color-mix(in srgb, var(--accent-color, #3b82f6) 10%, transparent);
  }

  .syncing .dot {
    animation: pulse 1.2s ease-in-out infinite;
  }

  .pending {
    color: var(--warning-color, #d97706);
    background: color-mix(in srgb, var(--warning-color, #d97706) 10%, transparent);
  }

  .offline {
    color: var(--text-muted, #9ca3af);
    background: color-mix(in srgb, var(--text-muted, #9ca3af) 8%, transparent);
  }

  .error {
    color: var(--error-color, #dc2626);
    background: color-mix(in srgb, var(--error-color, #dc2626) 10%, transparent);
  }

  /* --- Error popover --- */

  .error-popover {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 260px;
    background: var(--panel-background, #ffffff);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    padding: 12px;
    z-index: var(--z-index-dropdown, 100);
  }

  .error-popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .error-popover-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--error-color, #dc2626);
  }

  .error-close {
    background: none;
    border: none;
    font-size: 1.1rem;
    cursor: pointer;
    color: var(--text-muted, #9ca3af);
    padding: 0 2px;
    line-height: 1;
  }

  .error-close:hover {
    color: var(--text-primary, #1f2937);
  }

  .error-message {
    font-size: 0.75rem;
    color: var(--text-muted, #6b7280);
    margin: 0 0 10px;
    line-height: 1.4;
    word-break: break-word;
  }

  .error-retry {
    width: 100%;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 500;
    background: var(--error-color, #dc2626);
    color: #ffffff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .error-retry:hover:not(:disabled) {
    opacity: 0.9;
  }

  .error-retry:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* --- Animation --- */

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  /* Fallback for browsers without color-mix */
  @supports not (background: color-mix(in srgb, red 10%, transparent)) {
    .synced { background: rgba(22, 163, 106, 0.1); }
    .syncing { background: rgba(59, 130, 246, 0.1); }
    .pending { background: rgba(217, 119, 6, 0.1); }
    .offline { background: rgba(156, 163, 175, 0.08); }
    .error { background: rgba(220, 38, 38, 0.1); }
  }
</style>
